import { Address, AddressLookupService, valuesOf } from '@adreno-common';
import { Component, ChangeDetectionStrategy, Input } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { merge, Observable, of } from 'rxjs';
import {
  debounceTime,
  filter,
  map,
  scan,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { AddressLookupResult } from '../models';
import { FormBuilder, FormGroup } from '@angular/forms';

@Component({
  selector: 'app-address-lookup',
  templateUrl: './address-lookup.component.html',
  styles: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AddressLookupComponent {
  @Input()
  public set country(v: string) {
    this.formGroup.controls.country.patchValue(v);
  }

  @Input()
  public set initialAddress(v: string) {
    this.formGroup.controls.addressLookup.patchValue(v);
  }

  formGroup: FormGroup;
  viewModel$: Observable<ViewModel>;

  constructor(
    private activeModal: NgbActiveModal,
    private addressSvc: AddressLookupService,
    private toastrSvc: ToastrService,
    private fb: FormBuilder
  ) {
    this.formGroup = this.initFormGroup();
    this.viewModel$ = this.initViewModel();
  }

  private initFormGroup() {
    return this.fb.group({
      address: this.fb.control(undefined),
      addressLookup: this.fb.control(undefined),
      country: this.fb.control(undefined),
    });
  }

  private initViewModel() {
    return merge<Partial<ViewModel>>(
      valuesOf<string | undefined>(this.formGroup.controls.addressLookup).pipe(
        tap(() => this.formGroup.controls.address.setValue(undefined)),
        map(() => ({ addresses: undefined }))
      ),
      valuesOf<string | undefined>(this.formGroup.controls.addressLookup).pipe(
        debounceTime(500),
        withLatestFrom(
          valuesOf<string | undefined>(this.formGroup.controls.country).pipe(
            map((country) => country || 'Australia')
          )
        ),
        filter(
          ([addressLookup, country]) =>
            !!addressLookup && addressLookup.trim().length > 0
        ),
        switchMap(([addressLookup, country]) => {
          if (!this.addressSvc.hasLookup(country)) {
            return of([]);
          }
          return this.addressSvc.search(addressLookup!, country).pipe(
            map((vm) => {
              if (Array.isArray(vm)) {
                return vm;
              } else {
                this.toastrSvc.error(vm.message);
                return [];
              }
            })
          );
        }),
        map((addresses) => ({ addresses }))
      ),
      valuesOf<Address>(this.formGroup.controls.address).pipe(
        map((address) => ({ address: address || undefined }))
      )
    ).pipe(scan((acc, value) => ({ ...acc, ...value }), {} as ViewModel));
  }

  cancel() {
    this.activeModal.close({ action: 'cancel' } as AddressLookupResult);
  }

  select() {
    const address = this.formGroup.controls.address.value;
    if (!address) {
      this.toastrSvc.error('No address selected');
      return;
    }
    this.activeModal.close({
      action: 'select',
      address,
    } as AddressLookupResult);
  }

  setAddress(a: Address) {
    this.formGroup.controls.address.patchValue(a);
  }
}

interface ViewModel {
  address?: Address;
  addresses?: Address[];
}

interface FormModel {
  address?: Address;
  addressLookup: string;
  country: string;
}
